import {
	faEnvelope,
	faPlus,
	faStarHalfStroke,
} from '@fortawesome/pro-regular-svg-icons';
import { type Carrier } from '@uturn/api/v3';
import { LoadingSpan } from '@uturn/portal/components';
import { MessageCarrier } from '@uturn/portal/modules/shipments/components/message-carrier';
import { ReviewCarrierSheet } from '@uturn/portal/modules/shipments/components/shipment-sheet';
import type { CarrierReview } from '@uturn/portal/types/shipment';
import {
	Button,
	FormFieldReadOnly,
	Icon,
	Label,
	Sheet,
	SheetContent,
	SheetTrigger,
	Stars,
} from '@uturn/ui-kit';
import { useTranslation } from 'react-i18next';

export function CarrierInformation({
	shipmentNumber,
	carrier,
	carrierReview,
}: {
	shipmentNumber?: number;
	carrier: Carrier;
	carrierReview: CarrierReview;
}) {
	const { t } = useTranslation();

	if (!shipmentNumber) {
		return <LoadingSpan />;
	}
	return (
		<div className="grid w-full grid-cols-1 gap-5 sm:grid-cols-6">
			<div className="sm:col-span-full">
				<div className="flex flex-col gap-4">
					<div>
						<Label>
							{t(
								'pages.order.shipment-details.assigned-carrier.heading',
								'Carrier',
							)}
						</Label>
						<FormFieldReadOnly value={carrier.name} />
					</div>

					<div>
						<Label className="text-sm">
							{t(
								'pages.order.shipment-details.assigned-carrier.ean',
								'EAN code',
							)}
						</Label>
						<FormFieldReadOnly value={carrier.ean ?? '-'} />
					</div>

					<div className="flex flex-row pb-1">
						<Stars rating={Math.round(carrier.rating.averageRating)} />
						<span className="ml-2 mt-1 text-xs">
							{`(${t('general.reviews', {
								count: carrier.rating.totalRatings,
							})})`}
						</span>
					</div>

					<div className="flex gap-4">
						<Sheet>
							<SheetTrigger asChild>
								<Button
									variant="secondary"
									size="sm"
									className="flex items-center gap-2"
								>
									<Icon
										icon={faEnvelope}
										className="text-secondary-600 size-3.5"
									/>
									{t(
										'pages.order.shipment-details.assigned-carrier.actions.contact',
										'Contact',
									)}
								</Button>
							</SheetTrigger>
							<SheetContent>
								<MessageCarrier shipmentNumber={shipmentNumber} />
							</SheetContent>
						</Sheet>
						<Sheet>
							<SheetTrigger asChild>
								<Button
									variant="secondary"
									size="sm"
									className="flex items-center gap-2"
								>
									<Icon
										icon={!carrierReview ? faPlus : faStarHalfStroke}
										className="text-secondary-600 size-3.5"
									/>
									{!carrierReview
										? t(
												'pages.order.shipment-details.assigned-carrier.actions.review_add',
												'Add a review',
											)
										: t(
												'pages.order.shipment-details.assigned-carrier.actions.review_get',
												'Show my review',
											)}
								</Button>
							</SheetTrigger>
							<SheetContent>
								<ReviewCarrierSheet
									shipmentNumber={shipmentNumber}
									carrier={carrier}
									carrierReview={carrierReview}
								/>
							</SheetContent>
						</Sheet>
					</div>
				</div>
			</div>
		</div>
	);
}
